import { unix } from '@apparel/core/date-time';
import { Storage } from '@apparel/core/storage';
import { sha1 } from '@lib/hash';
import { localStorage } from '@lib/storages/localStorage';
import { memoryStorage } from '@lib/storages/memoryStorage';
import { sessionStorage } from '@lib/storages/sessionStorage';

const _DRIVERS = [localStorage, sessionStorage, memoryStorage];

export enum CacheDriver {
    LOCAL,
    SESSION,
    MEMORY,
}

export type TimeoutFunc = (
    key: string,
    value: {
        storeKey: string;
        key: string;
        value: any;
    },
) => void;

export type ReturnsFunc = (...args: any[]) => any;

export interface CacheStoreOptions {
    name?: string;
    driver?: CacheDriver;
    periodicClear?: number;
}

export class CacheStore {
    driver: Storage;
    options: CacheStoreOptions;

    constructor(options: CacheStoreOptions) {
        if (!options['_internal_']) {
            throw new Error("CacheStore shouldn't be called outside a CachePool, use createCache() instead!");
        }

        this.options = options;
        this.driver = _DRIVERS[options?.driver ?? CacheDriver.MEMORY];
    }

    /**
     * Store a value
     * @param key
     * @param value
     * @param timeout {number} Timeout in seconds
     * @returns {CacheStore}
     */
    put(key: string, value: any, timeout = 0): CacheStore {
        const storeKey = this.key(key);
        this.driver.setItem(storeKey, value);
        this.setTimeout(key, timeout || this.options.periodicClear);

        return this;
    }

    /**
     * Store a value forever
     * @param key
     * @param value
     * @returns {CacheStore}
     */
    forever(key: string, value: any): CacheStore {
        return this.put(key, value, -1);
    }

    /**
     * Get item
     * @param key
     * @returns {T}
     */
    get<T = any>(key: string): null | T {
        if (this.isExpired(key)) {
            return;
        }

        return this.driver.getItem<T>(this.key(key));
    }

    /**
     * Check if key exist
     * @param key
     * @returns {boolean}
     */
    exists(key: string): boolean {
        if (this.isExpired(key)) {
            return false;
        }

        return this.driver.hasItem(this.key(key));
    }

    /**
     *
     * @param key
     * @returns {CacheStore}
     */
    del(key: string): CacheStore {
        this.driver.removeItem(this.key(key));
        return this;
    }

    /**
     *
     * @param key
     * @returns {CacheStore}
     */
    destroy(key: string): CacheStore {
        return this.del(key);
    }

    /**
     *
     * @returns {CacheStore}
     */
    clear(): CacheStore {
        this.keys().forEach((key) => this.del(key));

        return this;
    }

    /**
     *
     * @returns {number}
     */
    size(): number {
        return this.keys().length;
    }

    /**
     * Return store name
     * @returns {*}
     */
    name(): string {
        return this.options?.name ?? 'Unknown';
    }

    /**
     * Get keys
     */
    keys(): string[] {
        // @ts-ignore
        return this.driver.keys().filter((key: string) => key && key.startsWith(this.key('')));
    }

    /**
     * create a uniq store key
     * @returns {string}
     * @param keyName
     */
    key(keyName: string): string {
        const start = `store_${this.name()}_`;

        if (keyName.startsWith(start)) {
            return keyName;
        }

        return `${start}${keyName}`;
    }

    /**
     * Returns cached value after the first call
     * @param key
     * @param func
     * @param args
     */
    async returns<T = any>(key: string, func: ReturnsFunc, ...args: any[]): Promise<T> {
        const hashID = `${this.key(key)}_returns_${sha1(args)}`;
        let value;

        if (!this.exists(hashID)) {
            value = await func(...args);

            if (value) {
                this.put(hashID, value);
            }
        } else {
            value = this.get<T>(hashID);
        }

        return value;
    }

    private setTimeout(key: string, timeout: number) {
        if (timeout > 0) {
            const storeKey = this.key(key);
            const timeouts = this.driver.getItem('__TIMEOUTS__') || {};
            timeouts[storeKey] = unix() + timeout * 1000;

            this.driver.setItem('__TIMEOUTS__', timeouts);
        }
    }

    private isExpired(key: string) {
        const storeKey = this.key(key);
        const timeouts = this.driver.getItem('__TIMEOUTS__') || {};

        if (timeouts && timeouts.hasOwnProperty(storeKey)) {
            const isExpired = unix() > parseInt(timeouts[storeKey]);
            if (isExpired) {
                this.destroy(key);
            }

            return isExpired;
        }

        return false;
    }
}
